import { DEX_NAMES } from 'multiswap/config'
import type { SourceResult, SourceStatus } from 'multiswap/engine'
import type { DexId } from 'multiswap/types'
import type { VenueOverride } from 'multiswap/venueOverride'
import { venueCount } from 'multiswap/venues'

/**
 * The price-hunt strip (Bez 2026-10-06: "a clean, beautiful verify/loading shown during search, with the logos of those exchanges, in
 * a slim strip while we're hunting the price, so the user feels what we did"). Pure model: what the strip says and how each logo
 * looks, from the comparison state. No React, no network.
 */

export const DEX_LOGOS: Record<DexId, string> = {
  cadinu: '/images/dex/hunt/cadinu.png',
  pancakeswap: '/images/dex/hunt/pancakeswap.png',
  uniswap: '/images/dex/hunt/uniswap.png',
  biswap: '/images/dex/hunt/biswap.png',
  bakeryswap: '/images/dex/hunt/bakeryswap.png',
}

export type HuntPhase = 'off' | 'idle' | 'single' | 'searching' | 'done' | 'none'
export type LogoState = 'idle' | 'pending' | 'ok' | 'answered' | 'failed' | 'winner' | 'muted'

export interface HuntQuote {
  dexId: DexId
  /** Raw router output (what the form shows as "receive"). */
  amountOut: bigint
  /** Output net of estimated gas, in the same token (null when gas could not be priced). */
  net: bigint | null
}

export interface HuntInput {
  /** Route comparison built in and the chain has sources. */
  enabled: boolean
  sources: DexId[]
  /** useRouteComparison().skip */
  skip: string | null
  /** The engine has no comparison yet for this pair + amount (first fetch). */
  engineLoading: boolean
  /** The standard CADINUdex trade is still loading. */
  standardLoading: boolean
  progress: Partial<Record<DexId, SourceStatus>>
  /** Results of the current comparison (null while the first fetch runs). */
  results: SourceResult[] | null
  /** Live, executable engine quotes. */
  live: HuntQuote[]
  /** The route the form will execute (null = no executable route). */
  winner: HuntQuote | null
  override: VenueOverride | null
  /**
   * Site review 59 #9 (2026-10-08): the USD value of ONE output token as the form already prices it (the "~ USD" under the amounts, the
   * same hook, no new request) and the token's decimals, so the finished strip can say what the lead is worth: "+$0.28 vs PancakeSwap".
   * Absent or null: the caption stays "vs PancakeSwap".
   */
  outUsdPerToken?: number | null
  outDecimals?: number
}

export interface HuntModel {
  phase: HuntPhase
  /** DEXes shown as logos (the sources), and how many of them answered: the chip and the progress line count these. */
  dexes: number
  answered: number
  /** Routes behind them (CADINUdex, PancakeSwap and Uniswap have a V2 and a V3 router each): 8 for the five DEXes. */
  routes: number
  logos: { dexId: DexId; name: string; src: string; state: LogoState }[]
  /** Title: a gold lead ("Best price:") and the rest. */
  lead: string
  title: string
  chip: { text: string; tone: 'count' | 'gain' | 'neutral' } | null
  caption: string
  /** What the finished strip claims, for the trade record (null in every other phase). */
  claim: HuntClaim | null
}

/** The strip's finished claim in whole basis points (floor): "+0.42%" = 42. The server treats it as untrusted (0–500, winner = verified DEX). */
export interface HuntClaim {
  winner: DexId
  runnerUp: DexId
  gainBps: number
  /** The winner's lead over PancakeSwap's best route, same comparison as gainBps; 0 when PancakeSwap wins; absent when it gave no quote. */
  vsPancakeBps?: number
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
/** "5 DEXes · 8 routes": exactly what the strip shows (one logo per DEX) and what is behind it. */
const scope = (d: number, r: number) => `${plural(d, 'DEX', 'DEXes')} · ${plural(r, 'route', 'routes')}`

/** Winner's lead over the runner-up, in percent with two decimals ("0.42"), "<0.01" for a positive sliver, null when not ahead. */
export function leadPercent(winnerOut: bigint, runnerOut: bigint): string | null {
  if (runnerOut <= 0n || winnerOut <= runnerOut) return null
  const bp = ((winnerOut - runnerOut) * 1_000_000n) / runnerOut // millionths
  if (bp < 100n) return '<0.01'
  const hundredths = bp / 100n
  return `${hundredths / 100n}.${(hundredths % 100n).toString().padStart(2, '0')}`
}

/**
 * The winner's lead in dollars: (winner - runner-up) output, in whole tokens, x the USD value of one token. Two decimals; a positive
 * sliver under a cent reads "<$0.01"; from $1,000 whole dollars with separators. null when there is no price, no lead, or nothing to compare.
 */
export function leadUsd(winnerOut: bigint, runnerOut: bigint, usdPerToken: number | null | undefined, decimals: number | undefined): string | null {
  if (!usdPerToken || !(usdPerToken > 0) || decimals === undefined || winnerOut <= runnerOut) return null
  const tokens = Number(winnerOut - runnerOut) / 10 ** decimals
  const usd = tokens * usdPerToken
  if (!Number.isFinite(usd) || usd <= 0) return null
  if (usd < 0.01) return '<$0.01'
  if (usd >= 1000) return `$${Math.round(usd).toLocaleString('en-US')}`
  return `$${usd.toFixed(2)}`
}

const SKIP_TEXT: Record<string, string> = {
  'fee-on-transfer': 'Fee-on-transfer token: one router only',
  'not-listed': 'Imported token: one router only',
  // short enough for the strip at 360 px (the route line under the form gives the full reason)
  'imported-checking': 'Checking token…',
  'imported-taxed': 'Transfer fee found',
  'imported-blocked': 'Test transfer failed',
  'imported-unknown': 'Token not checked',
  'listed-taxed': 'Transfer fee found',
  'listed-blocked': 'Test transfer failed',
  'exact-output': 'Type the amount you pay to compare',
}

const maxBy = <T>(xs: T[], v: (x: T) => bigint | null): T | null =>
  xs.reduce<T | null>((a, b) => (v(b) === null ? a : !a || (v(b) as bigint) > (v(a) as bigint) ? b : a), null)

/**
 * The finished state always states the gain (owner, 2026-10-06):
 *  - more tokens than the runner-up → "Best price: Biswap  +0.42%" ("best by <0.01%" under a hundredth of a percent);
 *  - the same tokens → "same price, lowest gas";
 *  - fewer tokens but chosen net of gas → "+0.03% after gas" against the best other route net of gas.
 */
function finished(w: HuntQuote, others: HuntQuote[], usd?: number | null, decimals?: number): { lead: string; chip: HuntModel['chip']; vs: DexId | null; usd: string | null } {
  const raw = maxBy(others, (o) => o.amountOut)
  if (!raw) return { lead: 'Best price:', chip: null, vs: null, usd: null }
  if (w.amountOut > raw.amountOut) {
    const l = leadPercent(w.amountOut, raw.amountOut) as string
    const dollars = leadUsd(w.amountOut, raw.amountOut, usd, decimals)
    return l === '<0.01' ? { lead: '', chip: { text: 'best by <0.01%', tone: 'gain' }, vs: raw.dexId, usd: dollars } : { lead: 'Best price:', chip: { text: `+${l}%`, tone: 'gain' }, vs: raw.dexId, usd: dollars }
  }
  if (w.amountOut === raw.amountOut) return { lead: '', chip: { text: 'same price, lowest gas', tone: 'count' }, vs: raw.dexId, usd: null }
  const net = maxBy(others, (o) => o.net)
  const l = w.net !== null && net && net.net !== null && net.net > 0n ? leadPercent(w.net, net.net) : null
  if (l === null) return { lead: '', chip: { text: 'lowest gas', tone: 'count' }, vs: (net ?? raw).dexId, usd: null }
  // the same comparison as the chip: net of gas, in the output token
  const dollars = leadUsd(w.net as bigint, (net as HuntQuote).net as bigint, usd, decimals)
  return { lead: '', chip: { text: l === '<0.01' ? 'best by <0.01%' : `+${l}% after gas`, tone: 'gain' }, vs: (net as HuntQuote).dexId, usd: dollars }
}

const bpsLead = (w: bigint, r: bigint): number => (r > 0n && w > r ? Number(((w - r) * 10_000n) / r) : 0)

/**
 * The claim behind the finished strip, the same comparison as `finished`: the winner's output against the best other output; when the
 * winner was chosen net of gas (fewer tokens), its net against the best other net; a tie or unknown gas is 0. No other route → null.
 * `vsPancakeBps` is that comparison against PancakeSwap's best route alone (0 when PancakeSwap wins, absent when it gave no quote).
 */
function lead(w: HuntQuote, others: HuntQuote[]): { vs: HuntQuote; bps: number } | null {
  const raw = maxBy(others, (o) => o.amountOut)
  if (!raw) return null
  if (w.amountOut >= raw.amountOut) return { vs: raw, bps: bpsLead(w.amountOut, raw.amountOut) }
  const net = maxBy(others, (o) => o.net)
  if (w.net === null || !net || net.net === null) return { vs: net ?? raw, bps: 0 }
  return { vs: net, bps: bpsLead(w.net, net.net) }
}

export function huntClaim(w: HuntQuote, others: HuntQuote[]): HuntClaim | null {
  const best = lead(w, others)
  if (!best) return null
  const claim: HuntClaim = { winner: w.dexId, runnerUp: best.vs.dexId, gainBps: best.bps }
  // the same comparison against PancakeSwap's best route only (2026-10-06, Markets "Smart Swap": better than PancakeSwap on N% of trades)
  if (w.dexId === 'pancakeswap') claim.vsPancakeBps = 0
  else {
    const pcs = lead(w, others.filter((o) => o.dexId === 'pancakeswap'))
    if (pcs) claim.vsPancakeBps = pcs.bps
  }
  return claim
}

export function huntModel(i: HuntInput): HuntModel {
  const dexes = i.sources.length
  const routes = venueCount(i.sources)
  const name = (d: DexId) => DEX_NAMES[d]
  const logo = (dexId: DexId, state: LogoState) => ({ dexId, name: name(dexId), src: DEX_LOGOS[dexId], state })
  const base = { dexes, routes, answered: 0, chip: null, lead: '', claim: null }
  const test = i.override ? ` · test: ${name(i.override.dexId)}${i.override.version ? ` ${i.override.version.toUpperCase()}` : ''} only` : ''

  if (!i.enabled || i.sources.length === 0 || i.skip === 'disabled' || i.skip === 'chain') {
    return { ...base, phase: 'off', logos: [], title: '', caption: '' }
  }
  if (i.skip === 'no-amount' || i.skip === 'same-token') {
    return {
      ...base,
      phase: 'idle',
      logos: i.sources.map((d) => logo(d, 'idle')),
      title: i.skip === 'same-token' ? 'Wrap or unwrap 1:1' : `Best price across ${scope(dexes, routes)}`,
      caption: i.skip === 'same-token' ? 'No exchange needed' : `Type an amount${test}`,
    }
  }
  if (i.skip) {
    return {
      ...base,
      phase: 'single',
      logos: i.sources.map((d) => logo(d, d === 'cadinu' ? 'ok' : 'muted')),
      lead: 'Route:',
      title: 'CADINUdex',
      caption: SKIP_TEXT[i.skip] ?? 'One router only',
    }
  }

  const statusOf = (d: DexId): SourceStatus | undefined => i.results?.find((r) => r.dexId === d)?.status ?? i.progress[d]
  const answered = i.sources.filter((d) => statusOf(d) !== undefined && !(d === 'cadinu' && i.standardLoading)).length

  if (i.engineLoading || i.standardLoading) {
    return {
      ...base,
      phase: 'searching',
      answered,
      logos: i.sources.map((d) => {
        const s = statusOf(d)
        if (d === 'cadinu' && i.standardLoading) return logo(d, 'pending')
        return logo(d, s === undefined ? 'pending' : s === 'ok' ? 'ok' : 'failed')
      }),
      title: `Checking ${scope(dexes, routes)}…`,
      chip: { text: `${answered}/${dexes}`, tone: 'count' },
      caption: `live quotes${test}`,
    }
  }

  const liveIds = new Set(i.live.map((l) => l.dexId))
  if (!i.winner) {
    return {
      ...base,
      phase: 'none',
      answered,
      logos: i.sources.map((d) => logo(d, liveIds.has(d) ? 'muted' : 'failed')),
      title: i.live.length ? 'No route under the 15% impact limit' : `No route on ${plural(dexes, 'DEX', 'DEXes')}`,
      caption: i.live.length ? 'Try a smaller amount' : `Checked ${plural(routes, 'route', 'routes')}${test}`,
    }
  }

  const w = i.winner
  // the winner is the one lit logo; the others that answered step back (full colour, quiet ring)
  const logos = i.sources.map((d) => logo(d, d === w.dexId ? 'winner' : statusOf(d) === 'ok' || liveIds.has(d) ? 'answered' : 'failed'))
  const others = i.live.filter((l) => l.dexId !== w.dexId)
  const f = finished(w, others, i.outUsdPerToken, i.outDecimals)
  return {
    ...base,
    claim: huntClaim(w, others),
    phase: 'done',
    answered,
    logos,
    lead: f.lead,
    title: name(w.dexId),
    chip: f.chip,
    // "+$0.28 vs PancakeSwap": the lead in dollars first (site review 59 #9), then who it is measured against
    caption: f.vs ? `${f.usd ? `+${f.usd} ` : ''}vs ${name(f.vs)}${test}` : `Only quote found${test}`,
  }
}
